/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* vastpersoneel.ts
* 14-1-2020 - Jelmer Jellema - Spin in het Web B.V.
*
* Twee tabs: de personeelslijst, en Functies en certificaten met een rij per functie per personeelslid.
*/

import {Datatable2scherm} from '../../abstracts/datatable2scherm.class';
import {Component} from '@angular/core';
import {DatatableDef} from 'sihw-ng/datatable2/datatable2.component';
import {Datarecords} from '../../../services/api/api.service';
import {Personeel} from '../../../models/personeel';
import {Leverancier} from '../../../models/leverancier';
import {DateTime} from 'luxon';

/**
 * Een rij in de tab Functies en certificaten: de raamovereenkomst of een functie van een personeelslid
 */
export interface Certificaatrij {
  sleutel: string; //uniek per rij, voor de tracker
  persoon: Personeel;
  functie: string; //'Raamovereenkomst' of de naam van de functie
  einddatum: string; //yyyy-mm-dd, of null als er geen einddatum is
  verlopen: boolean; //per vandaag
}

@Component({
  selector: 'scherm-vastpersoneel',
  templateUrl: './vastpersoneel.html',
  // styleUrls: ['./vastpersoneel.scss']
})
export class Vastpersoneelscherm extends Datatable2scherm {
  _starttitle = 'Personeel';
  icon = 'fas fa-people-carry';

  editscherm = 'Vastpersoneeledit';
  model = Personeel;
  //query nog op oude manier
  tableQuery = {
    table: 'personeelvast',
    connected: [
      {
        //alle functies, niet alleen de hoofdfunctie: we kijken ook naar de einddatums van de certificering
        table: 'personeelvast_functie',
        required: false,
        connected: [{
          table: 'functie',
          attributes: ['id', 'naam', 'kleur'] //niet via lookup, want we willen ook functies die niet kloppen toch tonen
        }]
      },
      {
        table: 'personeelvast_label'
      }
    ]
  };

  public actieveTab: 'personeel' | 'certificaten' = 'personeel';
  public certificaatData: Certificaatrij[] = []; //de rijen van de tab Functies en certificaten

//////////////////////////// tabledef
  public readonly tableDefinition: DatatableDef = {
    track: 'id',
    standaardsortering: {
      key: 'naam',
      richting: 1
    },
    //personeel met een verlopen certificering valt op, met de uitleg in de tooltip van de rij
    rijclasses: (persoon: Personeel) => persoon.certificeringVerlopen() ? ['certificering_verlopen'] : [],
    rijtitle: (persoon: Personeel) => persoon.certificeringmeldingen().join('\n'),
    velden: {
      naam: {
        label: 'Naam',
        prop: 'naam',
        type: 'string'
      },
      groep: {
        label: 'Groep',
        type: 'enum'
      },
      woonplaats: {
        label: 'Woonplaats',
        type: 'enum'
      },
      telefoonnummer: {
        label: 'Tel'
      },
      leverancier: {
        prop: '_tags.leverancier.naam',
        type: 'enum',
        label: 'Leverancier'
      },
      nummer: {
        label: 'Nr'
      },
      functiekleur: {
        label: null,
        prop: '_tags.hoofdfunctie.kleur',
        filter: false,
        exporthide: true,
        html: true,
        format: kleur => kleur ? `<div class="text-center"><span class='kleur' style="background-color: ${kleur};"></span></div>` : ''
      },
      functie: {
        label: 'Hoofdfunctie',
        prop: '_tags.hoofdfunctie.naam'
      },
      certificering: {
        label: 'Certificering',
        //een vinkje betekent hier, net als bij de andere janeevelden, dat het in orde is
        prop: (persoon: Personeel) => !persoon.certificeringVerlopen(),
        type: 'bool'
      },
      actief: {
        label: 'Inzetbaar',
        type: 'bool',
        standaardfilter: 1
      },
      inlog_actief: {
        label: 'Inloggen',
        type: 'bool'
      }
    }
  };

  //de rijen van een personeelslid staan in certificaatData al in de goede volgorde achter elkaar. Bij gelijke
  //sorteerwaarde houdt de datatable die volgorde aan, dus sorteren op naam laat ze bij elkaar staan
  public readonly certificaatDefinition: DatatableDef = {
    track: 'sleutel',
    standaardsortering: {
      key: 'naam',
      richting: 1
    },
    //een verlopen raamovereenkomst of functie valt op, met de datum in de tooltip van de rij
    rijclasses: (rij: Certificaatrij) => rij.verlopen ? ['certificering_verlopen'] : [],
    rijtitle: (rij: Certificaatrij) => rij.verlopen ? `Verlopen op ${DateTime.fromSQL(rij.einddatum).toFormat('dd-MM-yyyy')}` : '',
    velden: {
      naam: {
        label: 'Naam',
        prop: 'persoon.naam',
        type: 'string'
      },
      groep: {
        label: 'Groep',
        prop: 'persoon.groep',
        type: 'enum'
      },
      leverancier: {
        label: 'Leverancier',
        prop: 'persoon._tags.leverancier.naam',
        type: 'enum'
      },
      nummer: {
        label: 'Nr',
        prop: 'persoon.nummer'
      },
      functie: {
        label: 'Functie',
        type: 'enum'
      },
      einddatum: {
        label: 'Einddatum',
        type: ['moment', {format: 'DD-MM-YYYY', filtertype: 'datum'}]
      },
      geldig: {
        label: 'Geldig',
        //geldig zonder einddatum, of tot en met de einddatum
        prop: (rij: Certificaatrij) => !rij.verlopen,
        type: 'bool'
      },
      actief: {
        label: 'Inzetbaar',
        prop: 'persoon.actief',
        type: 'bool',
        standaardfilter: 1
      }
    }
  };

//constructor
  constructor() {
    super('debug');
  }

  //data aanpassen na ophalen
  protected async dataGetter(): Promise<Datarecords> {
    let res = <Personeel[]> await super.dataGetter();
    let leveranciers = await Leverancier.getLookup(Personeel);
    if (res) {
      for (let persoon of res) {
        //we halen alle functies op, dus we zoeken de hoofdfunctie erbij
        persoon._tags.hoofdfunctie = persoon.personeel_functie?.find(pf => pf.hoofdfunctie)?.functie;
        persoon._tags.leverancier = persoon.leverancier_id && leveranciers[persoon.leverancier_id];
      }
    }
    this.log.debug(res);
    return res;
  }

  /**
   * Bouwt na het ophalen de rijen van de tab Functies en certificaten op
   */
  protected override async afterHaaldata(): Promise<void> {
    this.certificaatData = (<Personeel[]> this.tableData).flatMap(persoon => this.certificaatrijen(persoon));
  }

  /**
   * De rijen van één personeelslid in de tab Functies en certificaten: eerst de raamovereenkomst (alleen als daar een
   * einddatum bij staat), dan de functies, de hoofdfunctie voorop en verder op naam.
   * @param persoon
   */
  private certificaatrijen(persoon: Personeel): Certificaatrij[] {
    const rijen: Certificaatrij[] = [];
    if (persoon.einddatum_certificering) {
      rijen.push({
        sleutel: `${persoon.id}-raamovereenkomst`,
        persoon: persoon,
        functie: 'Raamovereenkomst',
        einddatum: persoon.einddatum_certificering,
        verlopen: !persoon.certificeringGeldigOp()
      });
    }
    for (const pf of [...(persoon.personeel_functie || [])].sort((a, b) =>
      (Number(b.hoofdfunctie) - Number(a.hoofdfunctie)) || (a.functie?.naam || '').localeCompare(b.functie?.naam || ''))) {
      rijen.push({
        sleutel: `${persoon.id}-${pf.id}`,
        persoon: persoon,
        functie: pf.functie?.naam,
        einddatum: pf.einddatum_certificering,
        verlopen: !pf.geldigOp()
      });
    }
    return rijen;
  }
}
